import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getTodayLaunchActivity, getAllCustomers, updateLaunchActivity, deleteLaunchActivity } from '../../../firebase/services/customerVesselService';
import { getAllSlips } from '../../../firebase/services/slipService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const LaunchActivityPage: React.FC = () => {
  const [launchDate, setLaunchDate] = useState<Date>(new Date());
  const [launchActivity, setLaunchActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [vesselCustomerMap, setVesselCustomerMap] = useState<Record<string, { customer: any, vessel: any }> >({});
  const [vesselSlipMap, setVesselSlipMap] = useState<Record<string, string>>({});
  const [editForm, setEditForm] = useState<any>({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch customers and build vesselId map
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await getAllCustomers();
        const map: Record<string, { customer: any, vessel: any }> = {};
        customers.forEach(customer => {
          (customer.vessels || []).forEach(vessel => {
            map[vessel.vesselId] = { customer, vessel };
          });
        });
        setVesselCustomerMap(map);
      } catch (err) {
        // ignore for now
      }
    };
    fetchCustomers();
  }, []);

  // Fetch slips and build vesselId to slipNumber map
  useEffect(() => {
    const fetchSlips = async () => {
      try {
        const slips = await getAllSlips();
        const map: Record<string, string> = {};
        slips.forEach(slip => {
          if (slip.assignedVesselId && slip.slipNumber) {
            map[slip.assignedVesselId] = slip.slipNumber;
          }
        });
        setVesselSlipMap(map);
      } catch (err) {
        // ignore for now
      }
    };
    fetchSlips();
  }, []);

  // Replace getTodayLaunchActivity with a function to get all launch activity
  const getAllLaunchActivity = async (): Promise<any[]> => {
    const activitySnapshot = await getDocs(collection(db, 'launch_activity'));
    return activitySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate ? doc.data().timestamp.toDate() : new Date(doc.data().timestamp)
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all launch activity, then filter by selected date
        const allLaunches = await getAllLaunchActivity();
        const selectedDay = format(launchDate, 'yyyy-MM-dd');
        setLaunchActivity(
          allLaunches.filter(l => format(l.timestamp, 'yyyy-MM-dd') === selectedDay)
        );
      } catch (err: any) {
        setError('Failed to load launch activity.');
        setLaunchActivity([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [launchDate]);

  // When opening edit dialog, populate form
  useEffect(() => {
    if (editDialogOpen && selectedRecord) {
      const vesselInfo = vesselCustomerMap[selectedRecord.vesselId];
      const registration = vesselInfo ? vesselInfo.vessel.registrationNumber : (selectedRecord.registrationNumber || '');
      
      setEditForm({
        type: selectedRecord.type || '',
        staffName: selectedRecord.staffName || '',
        notes: selectedRecord.notes || '',
        trailerLicense: selectedRecord.trailerLicense || '',
        sealNumber: selectedRecord.sealNumber || '',
        sealColor: selectedRecord.sealColor || '',
        aisSticker: selectedRecord.aisSticker || '',
        registrationNumber: registration,
      });
      setEditError(null);
    }
  }, [editDialogOpen, selectedRecord, vesselCustomerMap]);

  const handleEditChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async () => {
    if (!selectedRecord?.id) return;
    setEditLoading(true);
    setEditError(null);
    try {
      await updateLaunchActivity(selectedRecord.id, editForm);
      setEditDialogOpen(false);
      // Refresh data
      const allLaunches = await getAllLaunchActivity();
      const selectedDay = format(launchDate, 'yyyy-MM-dd');
      setLaunchActivity(
        allLaunches.filter(l => format(l.timestamp, 'yyyy-MM-dd') === selectedDay)
      );
    } catch (err: any) {
      setEditError('Failed to update launch activity.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord?.id) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteLaunchActivity(selectedRecord.id);
      setDeleteDialogOpen(false);
      // Refresh data
      const allLaunches = await getAllLaunchActivity();
      const selectedDay = format(launchDate, 'yyyy-MM-dd');
      setLaunchActivity(
        allLaunches.filter(l => format(l.timestamp, 'yyyy-MM-dd') === selectedDay)
      );
    } catch (err: any) {
      setDeleteError('Failed to delete launch activity.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filtering
  const filtered = useMemo(() => {
    if (!search) return launchActivity;
    const s = search.toLowerCase();
    return launchActivity.filter(l => {
      const vesselInfo = vesselCustomerMap[l.vesselId];
      const customerName = vesselInfo ? `${vesselInfo.customer.firstName} ${vesselInfo.customer.lastName}` : '';
      const vesselName = vesselInfo ? vesselInfo.vessel.name : '';
      const slipNumber = vesselSlipMap[l.vesselId] || '';
      return (
        (l.vesselId || '').toLowerCase().includes(s) ||
        (l.registrationNumber || '').toLowerCase().includes(s) ||
        customerName.toLowerCase().includes(s) ||
        (l.staffName || '').toLowerCase().includes(s) ||
        (l.type || '').toLowerCase().includes(s) ||
        (l.notes || '').toLowerCase().includes(s) ||
        (vesselName || '').toLowerCase().includes(s) ||
        slipNumber.toLowerCase().includes(s)
      );
    });
  }, [launchActivity, search, vesselCustomerMap, vesselSlipMap]);

  // Sorting
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'timestamp') {
        aVal = a.timestamp?.getTime?.() || 0;
        bVal = b.timestamp?.getTime?.() || 0;
      } else if (sortBy === 'slip') {
        aVal = vesselSlipMap[a.vesselId] || '';
        bVal = vesselSlipMap[b.vesselId] || '';
      } else if (sortBy === 'sealNumber') {
        aVal = a.sealNumber || '';
        bVal = b.sealNumber || '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortBy, sortDir, vesselSlipMap]);

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Launch Activity
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <IconButton onClick={() => setLaunchDate(prev => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() - 1))}>
          <ArrowBackIcon />
        </IconButton>
        <DatePicker
          label="Select Date"
          value={launchDate}
          onChange={value => setLaunchDate(value instanceof Date ? value : new Date())}
          renderInput={params => <TextField {...params} />}
        />
        <IconButton onClick={() => setLaunchDate(prev => new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 1))}>
          <ArrowForwardIcon />
        </IconButton>
        <TextField
          label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {['timestamp', 'type', 'registrationNumber', 'sealNumber', 'slip', 'customerName', 'staffName', 'notes', 'actions'].map(col => (
                  <TableCell
                    key={col}
                    onClick={() => col !== 'actions' && handleSort(col)}
                    sx={{ cursor: col !== 'actions' ? 'pointer' : 'default', fontWeight: 'bold' }}
                  >
                    {col === 'timestamp' ? 'Time' :
                     col === 'type' ? 'Type' :
                     col === 'registrationNumber' ? 'Registration' :
                     col === 'sealNumber' ? 'Seal Number' :
                     col === 'slip' ? 'Slip' :
                     col === 'customerName' ? 'Customer' :
                     col === 'staffName' ? 'Staff' :
                     col === 'notes' ? 'Notes' :
                     'Actions'}
                    {sortBy === col && (sortDir === 'asc' ? ' ▲' : ' ▼')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">No launch activity for this date.</TableCell>
                </TableRow>
              ) : (
                sorted.map((activity, idx) => {
                  const vesselInfo = vesselCustomerMap[activity.vesselId];
                  let customerName = '-';
                  if (vesselInfo) {
                    const c = vesselInfo.customer;
                    customerName = c.companyName && c.companyName.trim() !== ''
                      ? c.companyName
                      : `${c.firstName} ${c.lastName}`;
                  }
                  const vesselName = vesselInfo ? vesselInfo.vessel.name : '';
                  const registration = vesselInfo ? vesselInfo.vessel.registrationNumber : (activity.registrationNumber || '-');
                  const slipNumber = vesselSlipMap[activity.vesselId] || '-';
                  return (
                    <TableRow key={activity.id || idx}>
                      <TableCell>{activity.timestamp ? format(activity.timestamp, 'hh:mm a') : '-'}</TableCell>
                      <TableCell>{activity.type}</TableCell>
                      <TableCell>{registration}</TableCell>
                      <TableCell>{activity.sealNumber || '-'}</TableCell>
                      <TableCell>{slipNumber}</TableCell>
                      <TableCell>{customerName}</TableCell>
                      <TableCell>{activity.staffName || '-'}</TableCell>
                      <TableCell>{activity.notes || '-'}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => { setSelectedRecord(activity); setEditDialogOpen(true); }}><EditIcon /></IconButton>
                        <IconButton size="small" color="error" onClick={() => { setSelectedRecord(activity); setDeleteDialogOpen(true); }}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Launch Activity</DialogTitle>
        <DialogContent sx={{ minWidth: 350 }}>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          <TextField
            label="Type"
            value={editForm.type || ''}
            onChange={e => handleEditChange('type', e.target.value)}
            select
            fullWidth
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="launch">Launch</option>
            <option value="haul">Haul</option>
          </TextField>
          <TextField
            label="Staff Name"
            value={editForm.staffName || ''}
            onChange={e => handleEditChange('staffName', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Notes"
            value={editForm.notes || ''}
            onChange={e => handleEditChange('notes', e.target.value)}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Trailer License"
            value={editForm.trailerLicense || ''}
            onChange={e => handleEditChange('trailerLicense', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Seal Number"
            value={editForm.sealNumber || ''}
            onChange={e => handleEditChange('sealNumber', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Seal Color"
            value={editForm.sealColor || ''}
            onChange={e => handleEditChange('sealColor', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="AIS Sticker"
            value={editForm.aisSticker || ''}
            onChange={e => handleEditChange('aisSticker', e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Registration Number"
            value={editForm.registrationNumber || ''}
            onChange={e => handleEditChange('registrationNumber', e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Launch Activity</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography>Are you sure you want to delete this launch activity record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaunchActivityPage; 